// src/components/hotel/ReservationTable.tsx

import React from 'react';
import { Link } from 'react-router-dom';
import { HotelDetailImageSlider } from '../monthly/HotelDetailImageSlider';
import { FloorPlanModal } from '../common/FloorPlanModal';
import { AmenityModal } from '../common/AmenityModal';

// 画像データの型
interface ImageData {
  src: string;
  alt: string;
  text: string;
}

// AvailabilityItem は Monthly版 と揃える (必要に応じて省略しないで定義)
interface AvailabilityItem {
  year_month: string;
  early: number;
  mid: number;
  late: number;
}

// props の定義
interface HotelReservationTableProps {
  /** 部屋名など */
  studioType: string;
  /** 物件ID (ホテルID) */
  propertyId: string;
  /** Chillnnの物件ID */
  propertyChillenId: string;
  /** Chillnnの部屋ID */
  roomChillenId: string;
  /** 画像配列 */
  images: ImageData[];
  /** 部屋の説明文 */
  roomDescription: string;
  /** カレンダー在庫まとめ (任意) */
  availabilityOverview?: AvailabilityItem[];
  /** 間取り図URL */
  floorPlanUrl?: string;
  /** 設備(日本語 or 英語) */
  facility?: string[];
  facility_en?: string[];
  /** バスアメニティ(日本語 or 英語) */
  bathAmenity?: string[];
  bathAmenity_en?: string[];
  /** 部屋ID */
  roomId?: string;
  /** 料金 */
  price?: number;
  /** 最寄り駅情報 */
  station_distance?: string;
  /** 表示切り替えフラグなど */
  isShow?: boolean;
  /** "BookNow" ボタンで部屋詳細を使うか */
  useRoomDetail?: boolean;
}

export const HotelReservationTable: React.FC<HotelReservationTableProps> = ({
  propertyId,
  propertyChillenId,
  roomChillenId,
  studioType,
  images,
  roomDescription,
  floorPlanUrl,
  facility,
  facility_en,
  bathAmenity,
  bathAmenity_en,
  price,
  station_distance,
}) => {
  // URLに `property` or `properties` が含まれる場合のみ "View Property" ボタンを表示
  const isPropertyPage =
    window.location.pathname.includes('property') ||
    window.location.pathname.includes('properties');

  // URLに "hotel/properties" を含む場合、Floor Plan と Amenities / Appliances は表示しない
  const isHotelPropertiesPage =
    window.location.pathname.includes('hotel/properties');

  // Book Now ボタン用リンク
  const bookNowLink = `https://www.chillnn.com/${propertyChillenId}/room/${roomChillenId}`;

  return (
    <div className="relative my-3">
      <div className="flex flex-col md:flex-row-reverse">
        {/* (1) 画像スライダー */}
        <div className="md:w-1/2">
          <HotelDetailImageSlider images={images} />
        </div>

        {/* (2) テキスト情報など */}
        <div className="md:w-1/2 flex flex-col justify-between">
          <div>
            <div className="mb-1 max-md:mt-4 mr-5">
              {/* 部屋の説明 */}
              <p
                className="text-[10px]"
                dangerouslySetInnerHTML={{ __html: roomDescription }}
              />
              {/* スタジオタイプ名 (部屋名) */}
              <p className="text-2xl mt-3">{studioType}</p>

              {/* 駅までの情報 */}
              <p className="text-xs mb-3">{station_distance}</p>

              {/* 料金 (一泊の目安) - 必要に応じてコメントアウト解除 */}
              {/* {price !== undefined && (
                <p className="text-base">{price}/Night ~</p>
              )} */}
            </div>

            {/* (3) ボタン類 */}
            <div className="flex md:mt-10 justify-center">
              {/* propertyページ遷移ボタン */}
              {isPropertyPage && (
                <Link
                  to={`/hotel/${propertyId}`}
                  className="text-black border border-black px-3 py-3 mb-6 mt-5 text-[15px] mr-2"
                >
                  View Property
                </Link>
              )}

              {/* Book Now ボタン → Chillnn 外部リンク */}
              <a
                href={bookNowLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white bg-black px-3 py-2 mb-6 mt-5 text-[15px] w-[120px] font-bold text-center"
              >
                Book Now
              </a>
            </div>
          </div>

          {/* (4) モーダル群: テキスト情報コンテナ内の最下部に配置 */}
          {!isHotelPropertiesPage && (
            <div className="flex text-xs max-md:text-left mt-4">
              <FloorPlanModal floorPlanUrl={floorPlanUrl} />
              <AmenityModal
                facility={facility}
                facility_en={facility_en}
                bathAmenity={bathAmenity}
                bathAmenity_en={bathAmenity_en}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
